import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useNavigationStatus } from '../hooks/useNavigationStatus';
import { useFocusTrap } from "../hooks/useFocusTrap";
import { API_BASE, authHeaders } from "../constants/api";
import { entityGroupError } from "../utils/thingsboardEdition";
import Pagination from "./Pagination";
import { useAuth } from "../context/AuthContext";
import "./DeviceGroups.css";
import { Hinzufügen, Hinweis, Bestätigen, Abbrechen, Suche } from '../icons';
import { useStatusMessage } from '../hooks/useStatusMessage';
import { StatusBar, DeleteConfirmModal, SortDropdown, IconButton, PageToolbar, ModalDeviceSearch } from '../UI-Elements';
import { generateSuggestions } from '../utils/suggestions';
import { formatDateDE } from '../utils/formatDate';

const PAGE_SIZE = 10;

function DeviceGroups() {
  const [groups, setGroups] = useState([]);
  const [groupCount, setGroupCount] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* Suche */
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(-1);
  const searchInputRef = useRef(null);

  /* Sortierung */
  const [sortOption, setSortOption] = useState("name");

  /* Löschen */
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { statusMessage, statusType, showStatus } = useStatusMessage();

  const [reloadKey, setReloadKey] = useState(0);
  const { authToken: token } = useAuth();
  const navigate = useNavigate();

  useNavigationStatus(showStatus);

  // New Group modal
  const [newGroupName, setNewGroupName] = useState("");
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [deviceSuggestions, setDeviceSuggestions] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [addingDevices, setAddingDevices] = useState(false);
  const [showAddDevicesTooltip, setShowAddDevicesTooltip] = useState(false);
  const [showDeviceSuggestions, setShowDeviceSuggestions] = useState(false);
  const tooltipRef = useRef(null);

  const newGroupModalRef = useRef(null);
  const deviceSuggestionsRef = useRef(null);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(-1);

  useFocusTrap(showNewGroupModal, newGroupModalRef);

  useEffect(() => {
    async function loadGroups() {
      try {
        setLoading(true);
        setError("");

        const sortMap = {
          name: { property: "name", order: "ASC" },
          created: { property: "createdTime", order: "DESC" },
          count: { property: "createdTime", order: "DESC" } // Platzhalter
        };

        const sort = sortMap[sortOption];

        const params = new URLSearchParams({
          pageSize: PAGE_SIZE,
          page,
          sortProperty: sort.property,
          sortOrder: sort.order,
          includeShared: false
        });

        if (searchTerm) {
          params.append("textSearch", searchTerm);
        }

        const res = await fetch(
          `${API_BASE}/entityGroups/DEVICE?${params.toString()}`,
          { headers: authHeaders(token) }
        );

        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(entityGroupError(
            res,
            errData?.detail || errData?.message || `Fehler beim Laden der Gruppen (HTTP ${res.status})`
          ));
        }
        const data = await res.json();

        const normalizedGroups = (data.data || [])
          .filter(g => g.name !== "All")
          .map(g => ({
            id: g.id.id,
            name: g.name,
            createdTime: g.createdTime,
            deviceCount: 0
          }));

        // Fetch device counts for each group in parallel
        const groupsWithCounts = await Promise.all(
          normalizedGroups.map(async grp => {
            try {
              const resp = await fetch(
                `${API_BASE}/entityGroup/${grp.id}/entities?pageSize=1&page=0`,
                { headers: authHeaders(token) }
              );
              if (!resp.ok) throw new Error();
              const cntData = await resp.json();
              return { ...grp, deviceCount: cntData.totalElements ?? 0 };
            } catch (e) {
              return { ...grp, deviceCount: 0 };
            }
          })
        );

        const visibleCount = groupsWithCounts.length;

        setGroups(groupsWithCounts);
        setGroupCount(visibleCount);
        setTotalPages(Math.ceil(visibleCount / PAGE_SIZE));
      } catch (err) {
        setError(err.message || "Fehler beim Laden der Gruppen. Bitte prüfen Sie Ihre Netzwerkverbindung.");
        setGroups([]);
        setGroupCount(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    }

    if (token) loadGroups();

  }, [token, page, sortOption, searchTerm, reloadKey]);

  const suggestions = generateSuggestions(groups, searchTerm);

  // Geräte-Suche für neues Group Modal
  useEffect(() => {
    if (!token) { setDeviceSuggestions([]); return; }

    const controller = new AbortController();

    async function searchDevices() {
      try {
        const params = new URLSearchParams({ pageSize: 10, page: 0 });
        if (deviceSearch) params.set('textSearch', deviceSearch);
        const res = await fetch(
          `${API_BASE}/tenant/devices?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal
          }
        );

        if (!res.ok) return;

        const data = await res.json();

        // Nur Geräte anzeigen, die nicht bereits ausgewählt wurden
        const selectedIds = new Set(selectedDevices.map(d => d.id));

        setDeviceSuggestions(
          (data.data || []).filter(d => !selectedIds.has(d.id.id))
        );
      } catch {}
    }

    searchDevices();
    return () => controller.abort();
  }, [deviceSearch, selectedDevices, token]);

    async function deleteGroup() {
      if (!groupToDelete) return;

      try {
        setDeleting(true);

        const res = await fetch(
          `${API_BASE}/entityGroup/${groupToDelete.id}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Gruppe konnte nicht gelöscht werden (HTTP ${res.status})`);
        }

        // Gruppe lokal aus Liste entfernen
        setGroups(prev => prev.filter(g => g.id !== groupToDelete.id));
        setGroupCount(c => Math.max(0, c - 1));

        showStatus(`Gruppe „${groupToDelete.name}" wurde erfolgreich gelöscht.`);

      } catch (e) {
        console.error(e);
        showStatus(`Gruppe konnte nicht gelöscht werden: ${e.message || 'Netzwerkfehler'}.`, 'error');
      } finally {
        setDeleting(false);
        setShowDeleteConfirm(false);
        setGroupToDelete(null);
      }
    }

    function addDevice(device) {
      setSelectedDevices(prev => [
        ...prev,
        { id: device.id.id, name: device.name }
      ]);
      setDeviceSearch("");
      setDeviceSuggestions([]);
      setHighlightedSuggestionIndex(-1);
    }

    function removeSelectedDevice(id) {
      setSelectedDevices(prev => prev.filter(d => d.id !== id));
    }

    async function createNewGroup() {
      if (!newGroupName.trim()) {
        showStatus("Bitte geben Sie einen Namen für die Gruppe ein.", 'error');
        return;
      }

      try {
        setAddingDevices(true);

        // 1. Neue Gruppe erstellen
        const createRes = await fetch(
          `${API_BASE}/entityGroup`,
          {
            method: "POST",
            headers: authHeaders(token),
            body: JSON.stringify({
              name: newGroupName.trim(),
              type: "DEVICE"
            })
          }
        );

        if (!createRes.ok) {
          const errData = await createRes.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Gruppe konnte nicht erstellt werden (HTTP ${createRes.status})`);
        }
        const groupData = await createRes.json();
        const newGroupId = groupData.id.id;

        // 2. Geräte zur Gruppe hinzufügen (falls vorhanden)
        if (selectedDevices.length > 0) {
          const deviceIds = selectedDevices.map(d => d.id);
          
          const addRes = await fetch(
            `${API_BASE}/entityGroup/${newGroupId}/addEntities`,
            {
              method: "POST",
              headers: authHeaders(token),
              body: JSON.stringify(deviceIds)
            }
          );

          if (!addRes.ok) {
            const errData = await addRes.json().catch(() => null);
            throw new Error(errData?.detail || errData?.message || `Geräte konnten nicht hinzugefügt werden (HTTP ${addRes.status})`);
          }
        }

        showStatus(`Gruppe „${newGroupName}" wurde erfolgreich erstellt.`);
        
        // Modal schließen und Formular zurücksetzen
        setShowNewGroupModal(false);
        setNewGroupName("");
        setSelectedDevices([]);
        setDeviceSearch("");
        setDeviceSuggestions([]);
        setHighlightedSuggestionIndex(-1);

        // Liste neu laden
        setPage(0);
        setReloadKey(k => k + 1);
      } catch (e) {
        console.error(e);
        showStatus(`Gruppe konnte nicht erstellt werden: ${e.message || 'Netzwerkfehler'}.`, 'error');
      } finally {
        setAddingDevices(false);
      }
    }

  return (
    <div>
      {/* Toolbar */}
      <PageToolbar
        left={
          <>
            <h1 className="device-title">Alle Gruppen ({groupCount ?? "..."})</h1>
            <SortDropdown
              options={[
                { key: 'name', label: 'Name' },
                { key: 'created', label: 'Hinzugefügt' },
                { key: 'count', label: 'Anzahl Geräte' },
              ]}
              value={sortOption}
              onChange={(key) => { setSortOption(key); setPage(0); }}
            />
          </>
        }
        right={
          <>
            <button className="export-button" aria-label="Neue Gruppe erstellen" title="Neue Gruppe erstellen"
              onClick={() => setShowNewGroupModal(true)}>
              <div className="export-icon"><Hinzufügen /></div>
              <div>Gruppe erstellen</div>
            </button>
            <div className="groups-search-wrapper">
              <label htmlFor="group-search" className="groups-search-label">Gruppe suchen</label>
              <span className="groups-search-icon"><Suche /></span>
              <input
                id="group-search"
                className="groups-search-input"
                ref={searchInputRef}
                value={searchTerm}
                autoComplete="off"
                aria-autocomplete="list"
                aria-controls="group-suggestions"
                aria-activedescendant={highlightedSuggestion >= 0 && showSuggestions ? `group-suggestion-${highlightedSuggestion}` : undefined}
                onChange={e => { setSearchTerm(e.target.value); setPage(0); setShowSuggestions(true); setHighlightedSuggestion(-1); }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={e => {
                  if (!showSuggestions || suggestions.length === 0) return;
                  if (e.key === "ArrowDown") { setHighlightedSuggestion(h => Math.min(h + 1, suggestions.length - 1)); e.preventDefault(); }
                  else if (e.key === "ArrowUp") { setHighlightedSuggestion(h => Math.max(h - 1, 0)); e.preventDefault(); }
                  else if (e.key === "Enter" && highlightedSuggestion >= 0) { setSearchTerm(suggestions[highlightedSuggestion].name); setShowSuggestions(false); setHighlightedSuggestion(-1); e.preventDefault(); }
                  else if (e.key === "Escape") { setShowSuggestions(false); setHighlightedSuggestion(-1); }
                }}
              />
              {searchTerm && (
                <button type="button" className="groups-search-clear" aria-label="Suche zurücksetzen"
                  onClick={() => { setSearchTerm(""); setShowSuggestions(false); setHighlightedSuggestion(-1); setPage(0); searchInputRef.current?.focus(); }}>✖</button>
              )}
              {showSuggestions && suggestions.length > 0 && (
                <div id="group-suggestions" className="groups-search-suggestions" role="listbox">
                  {suggestions.map((s, idx) => (
                    <div key={s.id} id={`group-suggestion-${idx}`}
                      className={`groups-search-suggestion ${highlightedSuggestion === idx ? "highlighted" : ""}`}
                      role="option" aria-selected={highlightedSuggestion === idx}
                      onMouseDown={() => { setSearchTerm(s.name); setShowSuggestions(false); setHighlightedSuggestion(-1); }}>
                      {s.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        }
      />
      
      <StatusBar message={statusMessage} statusType={statusType} />
      
      {/* Liste */}
      <div className="groups-table-container">
        {loading ? (
          <p>Gruppen werden geladen…</p>
        ) : error ? (
          <div className="error-message" role="alert">
            {error}
          </div>
        ) : (
          <>
            <div className="groups-list">
              <div className="groups-row groups-header">
                <div>Gruppenname</div>
                <div>Anzahl Geräte</div>
                <div>Hinzugefügt</div>
                <div>Öffnen</div>
                <div>Löschen</div>
              </div>

              {(!groups || groups.length === 0) ? (
                <div className="no-items" style={{ padding: '12px 20px', color: '#474747' }}>Keine Gruppen gefunden</div>
              ) : groups.map(g => (
                <div key={g.id} className="groups-row groups-item clickable-row"
                  onClick={(e) => {
                    if (e.target.closest('button, .sort-wrapper')) return;
                    navigate(`/device-groups/${g.id}`, { state: { name: g.name } });
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('button, .sort-wrapper')) {
                      e.preventDefault();
                      navigate(`/device-groups/${g.id}`, { state: { name: g.name } });
                    }
                  }}
                  tabIndex={0}
                  role="listitem"
                  aria-label={`Gruppe ${g.name} öffnen`}
                >
                  <div>{g.name}</div>
                  <div>{g.deviceCount}</div>
                  <div>{formatDateDE(g.createdTime)}</div>
                  <div className="cell-right">
                    <IconButton variant="open" label={`Gruppe ${g.name} öffnen`}
                      onClick={() => navigate(`/device-groups/${g.id}`, { state: { name: g.name } })} />
                  </div>
                  <div className="cell-right">
                    <IconButton variant="delete" label={`Gruppe ${g.name} löschen`}
                      onClick={() => { setGroupToDelete({ id: g.id, name: g.name }); setShowDeleteConfirm(true); }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {groupCount > 0 && (
              <Pagination page={page} totalPages={totalPages} totalItems={groupCount} pageSize={PAGE_SIZE} onPageChange={setPage} label="Gruppen" />
            )}
          </>
        )}
      </div>

      {showDeleteConfirm && groupToDelete && (
        <DeleteConfirmModal
          titleId="delete-confirm-title"
          title={<>Sind Sie sicher, dass Sie die Gruppe <br/><i>{groupToDelete.name}</i> löschen möchten?</>}
          body={<p>Nach der Bestätigung wird die Gruppe und alle zugehörigen Daten <strong>unwiderruflich</strong> gelöscht.</p>}
          onConfirm={deleteGroup}
          onCancel={() => { setShowDeleteConfirm(false); setGroupToDelete(null); }}
          loading={deleting}
        />
      )}

      {showNewGroupModal && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="add-devices-title">
          <div className="modal large" ref={newGroupModalRef}>

            <h2 id="add-devices-title">Neue Endgerätegruppe erstellen</h2>

            <h3 className="step-title"> 1. Name der Gruppe</h3>
            <div className="modal-device-search-wrapper">              
              <label htmlFor="device-search-input" className="modal-device-search-label">
                Name<span className="required">*</span>
              </label>
            
                <input
                  id="device-search-input"
                  className="modal-device-search-input"
                  value={newGroupName}
                  placeholder="Name eingeben"
                  autoFocus
                  onChange={(e) => setNewGroupName(e.target.value)}
                  maxLength="50"
                  required
                  title="Name der Gruppe eingeben"
                  aria-required="true"
                  aria-label="Name der Gruppe eingeben"
                />              
            </div>

            <p className="required-hint">* Pflichtfeld</p>

            {/* Suche */}
            <h3 className="step-title"> 2. Endgeräte hinzufügen
              <button
                type="button"
                className={`tooltip-icon-button ${showAddDevicesTooltip ? "active" : ""}`}
                aria-label="Informationen zum Hinzufügen von Endgeräten"
                aria-expanded={showAddDevicesTooltip}
                aria-controls="add-devices-tooltip"
                onClick={() => setShowAddDevicesTooltip(prev => !prev)}
                onKeyDown={e => {
                  if (e.key === "Escape") {
                    setShowAddDevicesTooltip(false);
                  }
                }}
              >
                <Hinweis />
              </button>
            </h3>

            {showAddDevicesTooltip && (
              <div
                id="add-devices-tooltip"
                role="tooltip"
                ref={tooltipRef}
                className="custom-tooltip"
              >
                Sie können direkt Endgeräte zu der neuen Gruppe hinzufügen. 
                Suchen Sie dafür nach eingebundenen Endgeräten über die Suchfunktion.  
                Dieser Schritt ist nicht verpflichtend und kann auch später in den Gruppendetails durchgeführt werden.
              </div>
            )}

            <ModalDeviceSearch
              searchValue={deviceSearch}
              onSearchChange={setDeviceSearch}
              suggestions={deviceSuggestions}
              showSuggestions={showDeviceSuggestions}
              onFocusShow={() => setShowDeviceSuggestions(true)}
              onBlurHide={() => setTimeout(() => setShowDeviceSuggestions(false), 150)}
              onHideSuggestions={() => setShowDeviceSuggestions(false)}
              highlightedIndex={highlightedSuggestionIndex}
              onHighlightChange={setHighlightedSuggestionIndex}
              onSelect={addDevice}
              onClearSearch={() => { setDeviceSearch(''); setHighlightedSuggestionIndex(-1); }}
              onEscapeClose={() => setShowNewGroupModal(false)}
              suggestionsRef={deviceSuggestionsRef}
              selectedDevices={selectedDevices}
              onRemoveDevice={removeSelectedDevice}
            />


            {/* Actions */}
            <div className="modal-actions">
              <button
                className="modal-primary-positive"
                onClick={createNewGroup}
                disabled={addingDevices || newGroupName.trim() === ""}
              >
                {addingDevices
                  ? "Gruppe wird erstellt…"
                  : <><Bestätigen /> Gruppe erstellen</>}
              </button>

              <button
                className="modal-secondary"
                onClick={() => {
                  setShowNewGroupModal(false);
                  setSelectedDevices([]);
                  setDeviceSearch("");
                  setHighlightedSuggestionIndex(-1);
                }}
                disabled={addingDevices}
              >
                <Abbrechen /> Abbrechen
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

export default DeviceGroups;
